import type { CSSProperties, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import AviatorIcon from '@/components/common/AviatorIcon';
import SportIcon from '@/components/common/SportIcon';
import { SPORTS } from '@/config/sports';
import { useInplayCounts } from '@/hooks/useInplayCounts';
import { useTranslation } from '@/i18n';

type StripItem = {
  key: string;
  label: string;
  to: string;
  active: boolean;
  count: number;
  icon: (style: CSSProperties) => ReactNode;
};

const ICON_ANIMATION = 'animate-nav-beat motion-reduce:animate-none';

/** Pulsing red live dot, the In-Play icon. */
const LiveIcon = ({ style }: { style: CSSProperties }) => (
  <span
    aria-hidden="true"
    className="relative flex size-6 items-center justify-center"
    style={style}
  >
    <span className="absolute size-4 animate-ping rounded-full bg-danger-red/50 motion-reduce:animate-none" />
    <span className="size-3 rounded-full bg-danger-red" />
  </span>
);

/**
 * Sticky sport switcher shown under the header on every page: In-Play, Aviator, then every
 * enabled sport, with live-match count bubbles. Icons pulse one after another.
 */
const SportStrip = () => {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const { counts, total } = useInplayCounts();
  const { t, tSport } = useTranslation();

  const items: StripItem[] = [
    {
      key: 'inplay',
      label: t('nav.inPlay'),
      to: '/home?view=inplay',
      active: pathname === '/home' && params.get('view') === 'inplay',
      count: total,
      icon: (style) => <LiveIcon style={style} />,
    },
    {
      key: 'aviator',
      label: t('nav.aviator'),
      to: '/casino?search=aviator',
      active: pathname === '/casino' && params.get('search')?.toLowerCase() === 'aviator',
      count: 0,
      icon: (style) => (
        <AviatorIcon
          aria-hidden="true"
          style={style}
          className={`size-6 text-danger-red sm:size-7 ${ICON_ANIMATION}`}
        />
      ),
    },
    ...SPORTS.filter((sport) => !sport.disabled).map((sport) => ({
      key: sport.apiName,
      label: tSport(sport.label),
      to: `/all-sports/${sport.apiName}`,
      active:
        decodeURIComponent(pathname).toLowerCase() === `/all-sports/${sport.apiName}`.toLowerCase(),
      count: counts[sport.apiName.toLowerCase()] ?? 0,
      icon: (style: CSSProperties) => (
        <span style={style} className={`flex ${ICON_ANIMATION}`}>
          <SportIcon name={sport.label} className="size-5 sm:size-6" />
        </span>
      ),
    })),
  ];

  return (
    <nav
      aria-label="Sports"
      className="flex gap-0.5 overflow-x-auto rounded-xl border border-shell-border bg-shell-panel p-1 [scrollbar-width:none] sm:gap-1 sm:p-2"
    >
      {items.map((item, index) => (
        <NavLink
          key={item.key}
          to={item.to}
          className={`relative flex w-[62px] shrink-0 flex-col items-center gap-1 rounded-lg px-1 pb-1.5 pt-2 text-center text-[10px] transition sm:w-[84px] sm:gap-1.5 sm:px-2 sm:pb-2 sm:pt-3 sm:text-xs ${
            item.active
              ? 'bg-shell-raised text-gold'
              : 'text-shell-fg/80 hover:bg-shell-raised hover:text-shell-fg'
          }`}
        >
          <span
            className={`relative flex size-9 items-center justify-center rounded-full border bg-shell sm:size-11 ${
              item.active ? 'border-gold' : 'border-shell-border'
            }`}
          >
            {/* Staggered start, so the icons pulse in a wave along the strip. */}
            {item.icon({ animationDelay: `${index * 0.12}s` })}
            {item.count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full bg-gold text-[9px] font-bold text-gold-foreground ring-2 ring-shell-panel sm:size-5 sm:text-[10px]">
                {item.count}
              </span>
            )}
          </span>
          <span className="w-full truncate">{item.label}</span>
          {item.active && (
            <span className="absolute inset-x-3 bottom-0 h-[3px] rounded-full bg-gold sm:inset-x-4" />
          )}
        </NavLink>
      ))}
    </nav>
  );
};

/** The strip in its sticky slot, right under the sticky header (whose height Header publishes). */
export const StickySportStrip = () => (
  <div className="sticky top-[var(--header-h,0px)] z-30 bg-shell px-3 pt-2 sm:pt-3 xl:pl-0">
    <SportStrip />
  </div>
);

export default SportStrip;
